import { createElement, StrictMode, Suspense, useEffect } from 'react';
import { Trans } from 'react-i18next';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useNavigate,
  useParams,
} from 'react-router';
import { AuthenticatedProvider } from './components/authenticated-provider';
import { getFirstNotDoneStep, steps } from './constants/steps';
import './i18n';
import { EnterInvite } from './pages/enter-invite';
import { InvalidInvite } from './pages/invalid-invite';
import { InviteComplete } from './pages/invite-complete';
import { InviteDeclined } from './pages/invite-declined';
import { InviteExpired } from './pages/invite-expired';
import { InviteLocked } from './pages/invite-locked';
import { Loading } from './pages/loading';
import { Code } from './pages/steps/code';
import { Welcome } from './pages/steps/welcome';
import { INVITE_STATUS, useInviteStore, useInviteSummary } from './store/use-invite-store';

const ROUTER = createBrowserRouter(
  [
    {
      path: '/',
      element: <Root />,
      children: [
        { index: true, element: <EnterInvite /> },
        {
          path: ':inviteId',
          element: <EnsureInvite />,
          children: [
            { index: true, element: <Navigate to="welcome" replace /> },
            { path: 'welcome', element: <Welcome /> },
            { path: 'code', element: <Code /> },
            { path: 'continue', element: <Continue /> },
            {
              element: <EnsureNonPartialInvite />,
              children: steps
                .filter((step) => step.component)
                .map((step, index) => ({
                  path: step.path,
                  element: createElement(step.component, {
                    ...step.props,
                    nextLabel: (
                      <Trans
                        i18nKey={step.nextLabel ?? `page.${step.path}:nextLabel`}
                        components={{ b: <b /> }}
                      />
                    ),
                    next: steps.at(index + 1) ?? null,
                    prev: steps.at(index - 1) ?? null,
                  }),
                })),
            },
          ],
          errorElement: <Navigate to="/" replace />,
        },
      ],
      errorElement: <Navigate to="/" replace />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

export function Root() {
  return <Outlet />;
}

export function App() {
  return (
    <StrictMode>
      <Suspense fallback={<Loading />}>
        <AuthenticatedProvider>
          <RouterProvider router={ROUTER} />
        </AuthenticatedProvider>
      </Suspense>
    </StrictMode>
  );
}

function EnsureInvite() {
  const { inviteId } = useParams();
  const { reference, status } = useInviteSummary();
  switch (status.toLowerCase()) {
    case INVITE_STATUS.CREATED:
    case INVITE_STATUS.STARTED:
      if (reference.toLowerCase() === inviteId.trim().toLowerCase()) {
        return <Outlet />;
      } else {
        console.warn(
          `Invite id mismatch (${reference.toLowerCase()} != ${inviteId.trim().toLowerCase()}), redirecting to root.`,
        );
        return <Navigate to="/" replace />;
      }
    case INVITE_STATUS.COMPLETED:
      return <InviteComplete />;
    case INVITE_STATUS.CANCELLED:
      return <InviteDeclined />;
    case INVITE_STATUS.LOCKED:
      return <InviteLocked />;
    case INVITE_STATUS.EXPIRED:
      return <InviteExpired />;
  }
  return <InvalidInvite />;
}

function Continue() {
  const { invite } = useInviteStore();
  const navigate = useNavigate();
  useEffect(() => {
    const step = getFirstNotDoneStep(invite.onlineBookingData, invite);
    navigate(`/${invite.reference}/${step.path}`, { replace: true });
  }, [invite, navigate]);
  return null;
}

function EnsureNonPartialInvite() {
  const { reference, partial } = useInviteSummary();
  if (partial) {
    return <Navigate to={`/${reference}/welcome`} replace />;
  } else {
    return <Outlet />;
  }
}
